import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Plus, Loader2, AlertTriangle, RefreshCw,
  Search, Filter, Clock, CheckCircle2, Circle, MapPin,
  FileText, ChevronRight, Archive,
} from 'lucide-react';

import { laudoApi } from '../../services/laudoApi';

/**
 * ============================================================
 * COMPONENTE: CentralVistorias
 * ------------------------------------------------------------
 * Lista/agenda de vistorias do perito.
 *   ✔ Carrega via laudoApi (retry + fila)
 *   ✔ Filtro por status + busca textual
 *   ✔ Estado vazio com CTA
 *   ✔ Navegação para criação e detalhe
 * VERSÃO: 2.0.0
 * ============================================================
 */

const STATUS_CONFIG = {
  ABERTA:       { label: 'Aberta',      cor: 'text-blue-400',    bg: 'bg-blue-500/10',    icon: Circle },
  EM_ANDAMENTO: { label: 'Em andamento',cor: 'text-amber-400',   bg: 'bg-amber-500/10',   icon: Clock },
  CONCLUIDA:    { label: 'Concluída',   cor: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle2 },
  ARQUIVADA:    { label: 'Arquivada',   cor: 'text-slate-400',   bg: 'bg-white/5',        icon: Archive },
};

const FILTROS = [
  { id: 'TODAS',        label: 'Todas' },
  { id: 'ABERTA',       label: 'Abertas' },
  { id: 'EM_ANDAMENTO', label: 'Em andamento' },
  { id: 'CONCLUIDA',    label: 'Concluídas' },
];

/* =========================
   COMPONENTE
========================= */

export default function CentralVistorias() {
  const navigate = useNavigate();
  const [vistorias, setVistorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('TODAS');

  /* ---------- carregamento ---------- */
  const carregar = async () => {
    setLoading(true);
    setErro(null);
    try {
      const dados = await laudoApi.listarVistorias();
      setVistorias(Array.isArray(dados) ? dados : []);
    } catch (err) {
      setErro(err.message || 'Falha ao carregar vistorias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  /* ---------- filtro + busca ---------- */
  const vistoriasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return vistorias.filter(v => {
      if (filtro !== 'TODAS' && v.status !== filtro) return false;
      if (!termo) return true;
      const campos = [
        v.titulo, v.processo, v.endereco, v.reportNumber, String(v.id),
      ].filter(Boolean).map(s => String(s).toLowerCase());
      return campos.some(c => c.includes(termo));
    });
  }, [vistorias, filtro, busca]);

  /* ---------- contadores ---------- */
  const contadores = useMemo(() => {
    const c = { TOTAL: vistorias.length };
    for (const s of Object.keys(STATUS_CONFIG)) {
      c[s] = vistorias.filter(v => v.status === s).length;
    }
    return c;
  }, [vistorias]);

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-200 font-black italic uppercase">

      <main className="flex-1 p-8 lg:p-12 overflow-y-auto">
        <div className="max-w-6xl mx-auto space-y-8">

          {/* HEADER */}
          <header className="flex flex-wrap justify-between items-start gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-amber-400">
                <Calendar size={24} />
                <span className="text-[10px] tracking-[0.5em] text-slate-500">
                  AGENDA DE CAMPO
                </span>
              </div>
              <h1 className="text-4xl text-white tracking-tight leading-none">
                Vistorias
              </h1>
              <p className="text-[10px] text-slate-500 tracking-[0.3em]">
                {contadores.TOTAL} registro(s) · {contadores.EM_ANDAMENTO || 0} em andamento
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={carregar}
                disabled={loading}
                className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all disabled:opacity-50"
                title="Recarregar"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>

              <button
                type="button"
                onClick={() => navigate('/vistorias/nova')}
                className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-amber-400 text-slate-950 text-[11px] tracking-widest hover:bg-white transition-all"
              >
                <Plus size={16} />
                NOVA VISTORIA
              </button>
            </div>
          </header>

          {/* BUSCA + FILTRO */}
          {!loading && vistorias.length > 0 && (
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-8 relative">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
                />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por título, processo, endereço ou ID..."
                  className="w-full h-12 bg-slate-900 border border-white/10 rounded-2xl pl-11 pr-4 text-white text-[12px] outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 normal-case"
                />
              </div>

              <div className="lg:col-span-4 flex gap-2 overflow-x-auto">
                {FILTROS.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFiltro(f.id)}
                    className={`px-4 py-3 rounded-2xl text-[10px] tracking-widest whitespace-nowrap transition-all ${
                      filtro === f.id
                        ? 'bg-amber-400 text-slate-950'
                        : 'bg-white/5 text-slate-400 hover:bg-white/10'
                    }`}
                  >
                    {f.label}
                    {contadores[f.id] !== undefined && (
                      <span className={`ml-2 text-[9px] ${
                        filtro === f.id ? 'text-slate-950/60' : 'text-slate-600'
                      }`}>
                        {contadores[f.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* ERRO */}
          {erro && (
            <div className="bg-red-950/50 border-2 border-red-500/30 rounded-2xl p-6 flex items-start gap-4">
              <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-3">
                <span className="text-red-200 text-[11px] tracking-widest leading-relaxed normal-case block">
                  {erro}
                </span>
                <button
                  type="button"
                  onClick={carregar}
                  className="bg-red-500/20 text-red-300 text-[10px] tracking-widest px-4 py-2 rounded-xl hover:bg-red-500/30 transition-all flex items-center gap-2"
                >
                  <RefreshCw size={12} /> TENTAR NOVAMENTE
                </button>
              </div>
            </div>
          )}

          {/* CONTEÚDO */}
          {loading ? (
            <div className="py-32 flex flex-col items-center gap-4">
              <Loader2 size={40} className="text-amber-400 animate-spin" />
              <p className="text-[10px] text-slate-500 tracking-[0.4em]">
                CARREGANDO VISTORIAS
              </p>
            </div>
          ) : vistoriasFiltradas.length === 0 ? (
            <EmptyState
              vazio={vistorias.length === 0}
              aoCriar={() => navigate('/vistorias/nova')}
              aoLimpar={() => { setBusca(''); setFiltro('TODAS'); }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {vistoriasFiltradas.map(v => (
                <CardVistoria
                  key={v.id}
                  vistoria={v}
                  onClick={() => navigate(`/vistorias/${v.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* =========================
   CARD
========================= */

function CardVistoria({ vistoria, onClick }) {
  const status = STATUS_CONFIG[vistoria.status] || STATUS_CONFIG.ABERTA;
  const Icon = status.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left bg-slate-900 border border-white/5 rounded-[2rem] p-6 hover:border-amber-500/40 hover:bg-slate-900/80 transition-all"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="space-y-1 min-w-0 flex-1">
          <span className="text-[9px] text-slate-500 tracking-[0.3em] block">
            {vistoria.reportNumber || `#${vistoria.id}`}
          </span>
          <h3 className="text-white text-[15px] tracking-tight leading-tight truncate normal-case">
            {vistoria.titulo || `Vistoria #${vistoria.id}`}
          </h3>
        </div>
        <span className={`${status.bg} ${status.cor} text-[9px] px-3 py-1.5 rounded-full tracking-widest flex items-center gap-2 shrink-0`}>
          <Icon size={10} />
          {status.label}
        </span>
      </div>

      <div className="space-y-2 text-[10px] tracking-wider">
        {vistoria.processo && (
          <div className="flex items-center gap-2 text-slate-400">
            <FileText size={12} className="text-slate-500" />
            <span className="truncate normal-case">{vistoria.processo}</span>
          </div>
        )}
        {vistoria.endereco && (
          <div className="flex items-center gap-2 text-slate-400">
            <MapPin size={12} className="text-slate-500" />
            <span className="truncate normal-case">{vistoria.endereco}</span>
          </div>
        )}
        {vistoria.dataVistoria && (
          <div className="flex items-center gap-2 text-slate-400">
            <Clock size={12} className="text-slate-500" />
            <span className="normal-case">
              {new Date(vistoria.dataVistoria).toLocaleString('pt-BR')}
            </span>
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
        <span className="text-[9px] text-slate-500 tracking-widest">
          {vistoria.perito || 'Perito'}
        </span>
        <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-amber-400 flex items-center justify-center transition-all">
          <ChevronRight
            size={14}
            className="text-slate-400 group-hover:text-slate-950 transition-colors"
          />
        </div>
      </div>
    </button>
  );
}

/* =========================
   ESTADO VAZIO
========================= */

function EmptyState({ vazio, aoCriar, aoLimpar }) {
  return (
    <div className="bg-white/[0.02] border-2 border-dashed border-white/10 rounded-[3rem] p-20 text-center space-y-6">
      <Calendar size={64} className="mx-auto text-slate-700" />
      <p className="text-slate-400 text-[12px] tracking-[0.3em]">
        {vazio ? 'NENHUMA VISTORIA AGENDADA' : 'NENHUM RESULTADO ENCONTRADO'}
      </p>
      <p className="text-slate-500 text-[10px] tracking-widest normal-case max-w-md mx-auto">
        {vazio
          ? 'Crie sua primeira vistoria para começar a coleta de dados em campo.'
          : 'Ajuste os filtros ou limpe a busca para ver todos os registros.'}
      </p>
      {vazio ? (
        <button
          type="button"
          onClick={aoCriar}
          className="px-8 py-4 rounded-2xl bg-amber-400 text-slate-950 text-[11px] tracking-widest hover:bg-white transition-all inline-flex items-center gap-3"
        >
          <Plus size={16} />
          CRIAR PRIMEIRA VISTORIA
        </button>
      ) : (
        <button
          type="button"
          onClick={aoLimpar}
          className="px-8 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[11px] tracking-widest hover:bg-white/10 transition-all inline-flex items-center gap-3"
        >
          <Filter size={16} />
          LIMPAR FILTROS
        </button>
      )}
    </div>
  );
}